import { useEffect, useRef, useState } from "react";
import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useT } from "@/lib/i18n";

// Prompt L — the ⓘ help window: hover (mouse), keyboard focus or tap opens it;
// leave, blur, Esc or an outside tap closes it. Never steals focus from a field.
export const FieldHelp = ({ k, label, items, testid }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const clear = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const later = (v, ms) => { clear(); timer.current = setTimeout(() => setOpen(v), ms); };
  useEffect(() => clear, []);
  const enter = (e) => { if (e.pointerType === "mouse") later(true, 100); };
  const leave = (e) => { if (e.pointerType === "mouse") later(false, 150); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${t("help.aria")} ${label}`}
          data-testid={testid}
          className="inline-flex items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-green)]"
          style={{ minWidth: 24, minHeight: 24, color: open ? "var(--brand-green)" : "var(--muted)" }}
          onPointerEnter={enter}
          onPointerLeave={leave}
          onFocus={(e) => { if (e.target.matches(":focus-visible")) { clear(); setOpen(true); } }}
          onBlur={() => later(false, 0)}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); clear(); setOpen((o) => !o); }}
        >
          <Info size={14} className="transition-colors hover:text-[var(--brand-green)]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={6}
        collisionPadding={16}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerEnter={clear}
        onPointerLeave={leave}
        data-testid={`${testid}-content`}
        className="data-[state=open]:animate-in data-[state=closed]:animate-out fade-in-0 fade-out-0 slide-in-from-bottom-1 motion-reduce:animate-none"
        style={{
          background: "var(--white)", border: "1px solid var(--hairline)", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)", padding: "12px 14px", fontSize: 12.5, lineHeight: 1.5,
          color: "var(--body)", width: "auto", maxWidth: "min(300px, calc(100vw - 32px))",
        }}
      >
        <p>{t(k)}</p>
        {items && items.length > 0 && (
          <ul className="mt-2 space-y-1">
            {items.map((it) => (
              <li key={it.id}><strong>{t(it.term)}</strong> — {t(it.text)}</li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};
